import { FC, MouseEvent } from "react";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  styled,
  useTheme,
} from "@mui/material";
import { formatScore } from "@context/utils";
import { Challenger, PdfChallenge, Score } from "@/models/pdfData";
import {
  Balance,
  Cancel,
  HideImage,
  Pending,
  ThumbDown,
  ThumbUp,
  Warning,
} from "@mui/icons-material";
import {
  challengeComplete,
  getChallengeTypeTranslation,
} from "@/utils/challenge-utils";
import {
  Challenge,
  CompletedChallenge,
  SamsChallengeOutcome,
  StartedChallenge,
  Teams,
} from "@/models/gameState";
import { gameState } from "@/services/gameState";

const smallIconStyling = {
  transform: "translate(-10px, 0px)",
  width: 14,
  height: 14,
};

const SmallThumbUp = styled(ThumbUp)(() => smallIconStyling);
const SmallThumbDown = styled(ThumbDown)(() => smallIconStyling);
const SmallCancel = styled(Cancel)(() => smallIconStyling);
const SmallPending = styled(Pending)(() => smallIconStyling);
const SmallBalance = styled(Balance)(() => smallIconStyling);
const SmallHideImage = styled(HideImage)(() => smallIconStyling);

const ChallengeListItem: FC<{
  selected: boolean;
  challenge: Challenge;
  isDeleted?: boolean;
  onClick: (challenge: Challenge) => void;
  handleContextMenu: (event: MouseEvent, challenge: Challenge) => void;
}> = ({ selected, challenge, isDeleted, onClick, handleContextMenu }) => {
  const { team1, team2 } = gameState("teams");
  const { palette } = useTheme();

  const teamIconUrls: { [key in Challenger]: string } = {
    team1: team1.logoImageMax400x400.url,
    team2: team2.logoImageMax400x400.url,
    referee: "/VBL_Logo.png",
  };

  const getIconForChallenge = (challenge: Challenge) => {
    const sx = {
      filter:
        palette.mode === "dark"
          ? "drop-shadow(0px 0px 2px black) drop-shadow(0px 0px 2px black)"
          : undefined,
    };

    if (!("durationMs" in challenge && challenge?.durationMs)) {
      return <SmallPending sx={sx} />;
    }

    if (challenge?.unresolved) {
      return <SmallCancel sx={sx} color={"warning"} />;
    }

    if (
      challenge?.challenger === "referee" &&
      !challenge.unresolved &&
      challenge.successful &&
      challenge.outcome !== "DOUBLE_FAULT"
    ) {
      return (
        <img
          style={smallIconStyling}
          src={
            teamIconUrls[
              challenge.outcome === "POINT_TEAM1" ? "team1" : "team2"
            ]
          }
        />
      );
    }

    if (challenge?.successful && challenge.outcome !== "DOUBLE_FAULT") {
      return <SmallThumbUp sx={sx} color={"success"} />;
    } else if (challenge.successful && challenge.outcome === "DOUBLE_FAULT") {
      return <SmallBalance />;
    } else if (challenge.unresolved) {
      return <SmallHideImage />;
    } else {
      return <SmallThumbDown sx={sx} color={"error"} />;
    }
  };

  const key = `challenge-item-${[challenge.setNumber, challenge.setScoreBeforeChallenge.team1, challenge.setScoreBeforeChallenge.team2, challenge.challenger].join("-")}`;

  return (
    <ListItemButton
      selected={selected}
      onClick={() => !isDeleted && onClick(challenge)}
      key={key}
      className={isDeleted ? "Mui-disabled" : ""}
      sx={{ pointerEvents: "auto !important" }}
      onContextMenu={(event) =>
        isDeleted && handleContextMenu(event, challenge)
      }
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={getIconForChallenge(challenge)}
      >
        <ListItemAvatar>
          <Avatar src={teamIconUrls[challenge.challenger]} />
        </ListItemAvatar>
      </Badge>

      <ListItemText
        primary={`${getScoreForChallenge(challenge)} (Satz ${challenge.setNumber})`}
        secondary={getChallengeTypeTranslation(challenge.challengeType)}
        slotProps={{
          primary: {
            color:
              ("outcome" in challenge && !!challenge.outcome) ||
              ("unresolved" in challenge && challenge.unresolved)
                ? palette.text.primary
                : palette.warning.main,
          },
        }}
      />
    </ListItemButton>
  );
};

const getScoreForChallenge = (challenge: Challenge): string => {
  const score =
    !("challengeMoment" in challenge) ||
    challenge.challengeMoment === "DURING_RALLY"
      ? challenge.setScoreBeforeChallenge
      : challenge.setScoreAfterChallenge;
  if (score) {
    return formatScore(score, challenge.challenger);
  } else {
    return "";
  }
};

export default ChallengeListItem;
